import { useEffect, useState } from 'react';
import { Input, Switch, Tooltip } from 'antd';
import FormDialog from '../../common/FormDialog';
import HFbutton from '../../common/input/HFbutton';
import TextWinds from '../../common/TextWinds';
import ErrorInput from '../../common/ErrorInput';
import { StudentStatusTag } from '../../../pages/student/class/StudentStatusTag';
import { LmsClassGroupType } from '../../../constants/types/lms-type';
import {
  StudentStatusEnum,
  studentType,
} from '../../../constants/types/student-type';
import { getStudentsByClassGroup } from '../../../api/utils/lms/class-group/api';
import {
  deleteClassGroup,
  updateClassGroup,
} from '../../../api/utils/lms/class-group/api';
import { getErrorMessages } from '../../../api/utils/util-func';
import { useAppDispatch } from '../../../store/hook';
import { openAlert, openConfirm } from '../../../store/features/modalSlice';
import {
  CLASS_GROUP_DESCRIPTION_MAX_LENGTH,
  CLASS_GROUP_DESCRIPTION_MESSAGE,
  CLASS_GROUP_DESCRIPTION_PLACEHOLDER,
  CLASS_GROUP_NAME_MAX_LENGTH,
  CLASS_GROUP_NAME_PLACEHOLDER,
  classGroupNameRules,
  isValidClassGroupDescription,
  isValidClassGroupName,
} from '../../../constants/validation';
import {
  DUPLICATED_CLASS_GROUP_NAME_MESSAGE,
  isDuplicatedClassGroupName,
} from './isDuplicatedClassGroupName';

interface ClassGroupDetailDialogProps {
  open: boolean;
  classId: string;
  /** 열려 있는 대상 반. 닫힌 상태에서는 null. */
  classGroup: LmsClassGroupType | null;
  /** 이름 중복 확인에 쓸 전체 반 목록 */
  classGroups: LmsClassGroupType[] | undefined;
  onClose: () => void;
  reloadClassGroups: () => void;
}

/**
 * 반 관리 > 반 하나의 정보(이름·설명·활성)를 고치고, 배정된 학생을 확인하고, 반을 삭제하는 모달.
 *
 * 목록 표에서는 인라인으로 고치던 것을 이 모달로 옮겨, 표는 한 줄에 반 하나만 보여 주고
 * 순서 변경(드래그)에 집중하도록 한다.
 */
const ClassGroupDetailDialog = ({
  open,
  classId,
  classGroup,
  classGroups,
  onClose,
  reloadClassGroups,
}: ClassGroupDetailDialogProps) => {
  const dispatch = useAppDispatch();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // 배정 학생: undefined = 조회 중, null = 조회 실패, 배열 = 결과
  const [students, setStudents] = useState<studentType[] | null | undefined>(
    undefined,
  );

  // 모달이 열릴 때마다 대상 반 기준으로 폼 값을 맞추고 학생 목록을 다시 불러온다.
  useEffect(() => {
    if (!open || !classGroup) return;

    setName(classGroup.name);
    setDescription(classGroup.description ?? '');
    setIsActive(classGroup.isActive);
    setError(null);
    setStudents(undefined);

    let alive = true;
    getStudentsByClassGroup(classId, classGroup.id)
      .then((res) => {
        if (alive) setStudents(res.result);
      })
      .catch((err) => {
        console.error(err);
        if (alive) setStudents(null);
      });

    return () => {
      alive = false;
    };
  }, [open, classGroup, classId]);

  if (!open || !classGroup) return null;

  const target = classGroup;

  /** 저장 전 이름·설명 검사. 통과하면 null, 아니면 화면에 띄울 메시지. */
  const validate = () => {
    const trimmed = name.trim();

    if (!trimmed) return '반 이름을 입력해주세요';
    if (!isValidClassGroupName(trimmed)) {
      const message = classGroupNameRules().validate.format(trimmed);
      return typeof message === 'string' ? message : '반 이름을 확인해주세요';
    }
    if (isDuplicatedClassGroupName(trimmed, classGroups, target.id)) {
      return DUPLICATED_CLASS_GROUP_NAME_MESSAGE;
    }
    if (!isValidClassGroupDescription(description)) {
      return CLASS_GROUP_DESCRIPTION_MESSAGE;
    }
    return null;
  };

  const saveHandler = async () => {
    const message = validate();
    setError(message);
    if (message) return;

    if (
      name.trim() === target.name &&
      description.trim() === (target.description ?? '').trim() &&
      isActive === target.isActive
    ) {
      dispatch(openAlert({ message: '변경된 내용이 없습니다.' }));
      return;
    }

    setIsSaving(true);
    try {
      await updateClassGroup(classId, target.id, {
        name: name.trim(),
        description: description.trim(),
        isActive,
      });
      reloadClassGroups();
      onClose();
      dispatch(openAlert({ message: '반 정보를 수정했습니다.' }));
    } catch (err) {
      dispatch(openAlert({ message: getErrorMessages(err) }));
    } finally {
      setIsSaving(false);
    }
  };

  const deleteHandler = () => {
    dispatch(
      openConfirm({
        message: (
          <div>
            <strong>{target.name}</strong> 반을 삭제할까요?
            <br />
            이 반에 배정된 학생들의 반 배정이 해제됩니다.
            <br />
            학생 계정과 학습 기록은 삭제되지 않아요.
          </div>
        ),
        confirmText: '삭제',
        onConfirm: async () => {
          try {
            await deleteClassGroup(classId, target.id);
            reloadClassGroups();
            onClose();
            dispatch(openAlert({ message: '반을 삭제했습니다.' }));
          } catch (err) {
            dispatch(openAlert({ message: getErrorMessages(err) }));
          }
        },
      }),
    );
  };

  const statusOf = (student: studentType): StudentStatusEnum | undefined =>
    student.classInfos?.find((info) => info.classId === classId)?.studentStatus;

  const studentListBody = () => {
    if (students === undefined) {
      return (
        <div className="flex h-[120px] items-center justify-center">
          <TextWinds type="content_body3" color="gray6">
            학생 목록을 불러오는 중이에요…
          </TextWinds>
        </div>
      );
    }
    if (students === null) {
      return (
        <div className="flex h-[120px] items-center justify-center">
          <TextWinds type="content_body3" color="gray6">
            학생 목록을 불러오지 못했어요.
          </TextWinds>
        </div>
      );
    }
    if (students.length === 0) {
      return (
        <div className="flex h-[120px] items-center justify-center">
          <TextWinds type="content_body3" color="gray6">
            배정된 학생이 없어요.
          </TextWinds>
        </div>
      );
    }
    return (
      <ul className="flex flex-col divide-y divide-gray2">
        {students.map((student) => {
          const status = statusOf(student);
          return (
            <li
              key={student.id}
              className="flex items-center justify-between gap-2 px-3 py-2"
            >
              <div className="flex min-w-0 flex-col">
                <TextWinds
                  type="content_body3"
                  color="gray9"
                  className="truncate font-medium"
                >
                  {student.name}
                </TextWinds>
                <TextWinds type="caption1" color="gray6" className="truncate">
                  {student.uid}
                </TextWinds>
              </div>
              {status && <StudentStatusTag studentStatus={status} />}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <FormDialog
      open={open}
      title="반 관리"
      onClose={onClose}
      footer={
        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          <HFbutton onClick={saveHandler} disabled={isSaving}>
            저장
          </HFbutton>
          <HFbutton type="Outlined" onClick={onClose} disabled={isSaving}>
            취소
          </HFbutton>
        </div>
      }
    >
      <div className="flex flex-col gap-5">
        {/* 반 정보 수정 */}
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-x-1.5">
              <TextWinds type="content_body3" color="gray7">
                반 이름
              </TextWinds>
              <span className="select-none rounded bg-gray2 px-1.5 py-0.5 text-xs text-gray7">
                필수
              </span>
            </div>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onPressEnter={saveHandler}
              status={error ? 'error' : undefined}
              placeholder={CLASS_GROUP_NAME_PLACEHOLDER}
              maxLength={CLASS_GROUP_NAME_MAX_LENGTH}
              showCount
              autoComplete="off"
              disabled={isSaving}
              className="rounded"
            />
          </div>

          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-x-1.5">
              <TextWinds type="content_body3" color="gray7">
                활성 상태
              </TextWinds>
              <span className="select-none rounded bg-gray2 px-1.5 py-0.5 text-xs text-gray7">
                필수
              </span>
            </div>
            <Tooltip title="비활성 반은 학생을 배정하거나 수강생으로 전환할 수 없어요">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                <Switch
                  size="default"
                  checked={isActive}
                  onChange={setIsActive}
                  disabled={isSaving}
                  // 비활성일 때 antd 기본 트랙은 옅은 회색이라 흰색 '비활성' 글자가 묻힌다.
                  style={isActive ? undefined : { backgroundColor: '#868E96' }}
                  checkedChildren={
                    <TextWinds type="content_body3" color="white">
                      활성
                    </TextWinds>
                  }
                  unCheckedChildren={
                    <TextWinds type="content_body3" color="white">
                      비활성
                    </TextWinds>
                  }
                />
                {!isActive && (
                  <TextWinds type="content_body3" color="gray6">
                    비활성 반에는 학생을 새로 배정할 수 없어요. <br />
                    비활성 반에 속해있는 학생은 모두{' '}
                    <span className="font-bold">정지</span> 상태로 변경돼요.
                  </TextWinds>
                )}
              </div>
            </Tooltip>
          </div>

          <div className="flex flex-col gap-2">
            <TextWinds type="content_body3" color="gray7">
              반 설명
            </TextWinds>
            <Input.TextArea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={CLASS_GROUP_DESCRIPTION_PLACEHOLDER}
              maxLength={CLASS_GROUP_DESCRIPTION_MAX_LENGTH}
              showCount
              rows={2}
              autoComplete="off"
              disabled={isSaving}
              className="rounded"
            />
          </div>

          {error && <ErrorInput text={error} />}
        </div>

        {/* 배정된 학생: 읽기 전용 정보라 편집 영역과 구분선으로 떼어 놓는다. */}
        <div className="flex flex-col gap-2 border-t border-gray2 pt-4">
          <div className="flex items-center justify-between">
            <TextWinds
              type="content_body3"
              color="gray7"
              className="font-medium"
            >
              이 반에 배정된 학생
            </TextWinds>
            <TextWinds type="content_body3" color="gray7" className="shrink-0">
              {Array.isArray(students) ? `${students.length}명` : ''}
            </TextWinds>
          </div>
          <div className="max-h-[240px] overflow-y-auto rounded border border-gray3">
            {studentListBody()}
          </div>
        </div>

        {/* 위험 구역: 주 액션(저장)과 떼어 놓는다. */}
        <div className="flex items-center justify-between gap-3 border-t border-gray2 pt-4">
          <TextWinds type="content_body3" color="gray7">
            이 반을 삭제하면 배정된 학생의 반 배정이 해제돼요.
          </TextWinds>
          <div className="shrink-0">
            <HFbutton
              type="orange-Outlined"
              width="auto"
              height={36}
              onClick={deleteHandler}
              disabled={isSaving}
            >
              반 삭제
            </HFbutton>
          </div>
        </div>
      </div>
    </FormDialog>
  );
};

export default ClassGroupDetailDialog;
